import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SearchSummary } from 'projects/reg-hub-common/src/lib/interfaces/search-summaries/search-summary';
import { eOrderStatusType, eSummaryEntityType, Order, OrderManagerService, PpsaSummaryResult, SearchResultsRepositoryService, SlideDownAnimation } from 'reg-hub-common';
import { concatMap, first, Subject, takeUntil, tap, timer } from 'rxjs';
import { SearchSummaryResultComponent } from '../search-summary-result/search-summary-result.component';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'app-search-summary',
    templateUrl: './search-summary.component.html',
    styleUrl: './search-summary.component.css',
    animations: [
        SlideDownAnimation
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SearchSummaryComponent implements OnInit, OnDestroy {
  private readonly transloco = inject(TranslocoService);
  protected order: Order | undefined;
  protected summary: SearchSummary | undefined;

  protected highlightedResults: PpsaSummaryResult[] = [];
  protected hoveredResult: PpsaSummaryResult | undefined | null;

  private onDestroy$ = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    private searchResultsService: SearchResultsRepositoryService,
    private orderManager: OrderManagerService) { }

  ngOnInit(): void {
    this.orderManager.currentOrder
      .pipe(
        takeUntil(this.onDestroy$),
        tap(order => this.order = order),
        first(order => order.orderStatusTypeID === eOrderStatusType.complete),
        concatMap(order => this.searchResultsService.getSearchSummaryAsJson(order.id, { hideErrorsStatusCodes: [404] })))
      .subscribe(summary => {
        this.summary = summary;
      });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  open(result: PpsaSummaryResult) {
    this.dialog.open(SearchSummaryResultComponent, {
      data: { result: result, order: this.order },
      minWidth: '1050px'
    })
  }

  getRemainingRegistrationPeriod(result: PpsaSummaryResult) {
    const to = new Date(result.to!);
    const now = new Date();
    const remaining = to.getTime() - now.getTime();

    if (remaining <= 0) {
      return this.transloco.translate('orders.expired');
    } else {
      return this.transloco.translate('orders.nYears', { n: (remaining / 31536000000).toFixed(1) });
    }
  }

  getFirstDebtorName(result: PpsaSummaryResult) {
    return result.entities?.filter(e => e.entityType === eSummaryEntityType.debtor).at(0)?.identifier ??
      result.entities?.filter(e => e.entityType === eSummaryEntityType.transferor).at(0)?.identifier ??
      result.entities?.filter(e => e.entityType === eSummaryEntityType.transferee).at(0)?.identifier;
  }

  getFirstSecuredPartyName(result: PpsaSummaryResult) {
    return result.entities?.filter(e => e.entityType === eSummaryEntityType.securedParty).at(0)?.identifier ??
      result.entities?.filter(e => e.entityType === eSummaryEntityType.assignor).at(0)?.identifier ??
      result.entities?.filter(e => e.entityType === eSummaryEntityType.assignee).at(0)?.identifier;
  }

  shouldShowRegistrationExpiryValue(result: PpsaSummaryResult) {
    return result.from !== null && result.from !== undefined && result.to !== null && result.to !== undefined || result.isInfiniteTerm;
  }

  getExpiryRegistrationValue(result: PpsaSummaryResult) {
    if (result.isInfiniteTerm) {
      return 0;
    } else {
      const from = new Date(result.from!);
      const to = new Date(result.to!);
      const total = to.getTime() - from.getTime();
      const now = new Date();
      const elapsed = now.getTime() - from.getTime();
      const percentage = Math.floor((elapsed / total) * 100);
      return percentage;
    }
  }

  onResultMouseEnter(result: PpsaSummaryResult) {
    if (this.summary?.ppsaSummaryResults?.some(r => r.familyNumber !== null)) {
      this.hoveredResult = result;
      this.highlightedResults = this.summary?.ppsaSummaryResults?.filter(r => r.familyNumber === result.familyNumber) ?? [];
    }
  }

  onResultMouseLeave(result: PpsaSummaryResult) {
    this.hoveredResult = null;
    this.highlightedResults = [];
  }

  shouldHighlightResult(result: PpsaSummaryResult) {
    return this.highlightedResults.findIndex(r => r.id === result.id) !== -1;
  }

  combineStolenJurisdictions() {
    return this.summary?.reportedStolenIn?.join(', ');
  }
}
